import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import WaveSurfer from 'wavesurfer.js';
import Hover from 'wavesurfer.js/dist/plugins/hover.js';
import noop from 'lodash/noop';

import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';

import PlayerButtons from './PlayerButtons';
import alertsUtil from '@/util/alertsUtil';
import { dialogTypes } from '@/constants/alertTypes';
import './AudioPlayer.scss';

const AudioPlayer = ({
  autoPlay,
  height,
  src,
  onLoad,
  onError,
  isFullScreen,
  setCurrentPlayingAudio
}) => {
  const dispatch = useDispatch();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const playerEl = useRef(null);
  const wavesurferRef = useRef(null);

  const formatTime = time => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60)
      .toString()
      .padStart(2, '0');
    const milliseconds = Math.floor((time % 1) * 1000)
      .toString()
      .padStart(3, '0');
    return `${minutes}:${seconds}.${milliseconds}`;
  };

  useEffect(() => {
    if (!playerEl.current) return;

    const wavesurfer = WaveSurfer.create({
      container: playerEl.current,
      height,
      waveColor: '#98C4EF',
      progressColor: '#5899DA',
      cursorWidth: 0,
      cursorColor: 'transparent',
      responsive: true,
      fetchParams: { credentials: 'include' },
      plugins: [
        Hover.create({
          lineColor: '#000',
          lineWidth: 1,
          labelBackground: '#000',
          labelColor: '#fff',
          labelSize: '12px',
          formatTimeCallback: formatTime
        })
      ]
    });

    wavesurfer.on('pause', () => {
      wavesurfer.setOptions({ cursorWidth: 1, cursorColor: '#5899DA' });
      setIsPlaying(false);
    });

    wavesurfer.on('play', () => {
      setCurrentPlayingAudio(wavesurfer);

      wavesurfer.setOptions({ cursorWidth: 0, cursorColor: 'transparent' });
      setIsPlaying(true);
    });

    wavesurfer.on('ready', handleReady);
    wavesurfer.on('timeupdate', setCurrentTime);
    wavesurfer.on('finish', () => {
      wavesurfer.setOptions({ cursorWidth: 0, cursorColor: 'transparent' });
      wavesurfer.seekTo(0);
    });

    wavesurfer.on('error', error => {
      handleError(error);
    });

    wavesurferRef.current = wavesurfer;
  }, [height]);

  const handleError = error => {
    if (onError) onError(error);
    dispatch(
      alertsUtil.openErrorDialog(
        dialogTypes.CATCH_ERROR_WAVESURFER_AUDIO,
        error
      )
    );
  };

  useEffect(() => {
    if (!src || !wavesurferRef.current) return;

    setIsReady(false);
    try {
      wavesurferRef.current.load(src);
    } catch (error) {
      console.error('Failed to load audio file:', error);
      handleError(error);
    }
  }, [src]);

  const handlePause = () => {
    wavesurferRef.current?.pause();
  };

  const handlePlay = () => {
    wavesurferRef.current?.play();
  };

  const handleReady = () => {
    onLoad();
    wavesurferRef.current.seekTo(0);
    setIsReady(true);

    if (autoPlay) {
      wavesurferRef.current?.play();
    }
  };

  let buttonsOrLoading;

  if (isReady) {
    buttonsOrLoading = (
      <div
        className={`audio-player-buttons ${
          isFullScreen ? 'button-full-screen' : ''
        }`}
      >
        <PlayerButtons
          isPlaying={isPlaying}
          onPause={handlePause}
          onPlay={handlePlay}
        />
        {isFullScreen ? (
          <span className="player-timer">{formatTime(currentTime)}</span>
        ) : null}
      </div>
    );
  } else {
    buttonsOrLoading = (
      <Grid
        className="audio-player-loading"
        alignItems="center"
        justify="center"
        container
      >
        <CircularProgress />
      </Grid>
    );
  }

  const visibility = isReady ? 'visible' : 'hidden';
  const playerClassName = isFullScreen
    ? 'audio-player full-screen'
    : 'audio-player default';

  return (
    <div className={playerClassName}>
      {buttonsOrLoading}
      <div ref={playerEl} id="waveform" style={{ visibility }} />
    </div>
  );
};

AudioPlayer.defaultProps = {
  autoPlay: false,
  height: 90,
  onError: noop,
  onLoad: noop,
  isFullScreen: false,
  setCurrentPlayingAudio: noop
};

AudioPlayer.propTypes = {
  autoPlay: PropTypes.bool,
  height: PropTypes.number,
  onError: PropTypes.func,
  onLoad: PropTypes.func,
  src: PropTypes.string.isRequired,
  isFullScreen: PropTypes.bool,
  setCurrentPlayingAudio: PropTypes.func
};

export default AudioPlayer;
